import type { ReactNode } from "react";
import { Field, RecordList } from "@cntrl/ui";
import styles from "./ExpenseLinesList.module.scss";

/** One editable custom expense line (`show_expense_lines`) — strings while typed, converted at save. A STORED line
 *  carries its `lineId` back so the save keeps it (and what the editor never edits rides along server-side); a line
 *  a crew member added from the Calendar also says who added it (ART-305 v2 — who to reimburse). */
export type ExpenseLineDraft = {
  label: string; amount: string; needsReimbursement: boolean;
  lineId?: string; addedByName?: string | null; hasReceipt?: boolean;
};

/**
 * The custom expense lines, as ONE list on both surfaces that write them — the editor's 09 Expenses and the
 * settlement panel's Add-expense dialog. label · amount · "Needs reimbursement" in a reorderable `RecordList`.
 *
 * Both saves full-replace (or append to) `show_expense_lines` and the server derives `position` from array order,
 * so a move is purely a splice of this array. A stored row round-trips its `lineId`, which keeps the row (its
 * Outstanding Payments "paid" marker and who added it) rather than re-creating it.
 */
export function ExpenseLinesList({ rows, onChange, addLabel, empty }: {
  rows: readonly ExpenseLineDraft[];
  onChange: (next: ExpenseLineDraft[]) => void;
  addLabel: string;
  empty?: ReactNode;
}) {
  const patch = (i: number, p: Partial<ExpenseLineDraft>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)));
  function move(from: number, to: number) {
    if (from === to || to < 0 || to >= rows.length) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  }
  return (
    <RecordList
      dragKind="show-expense"
      items={rows}
      addLabel={addLabel}
      empty={empty}
      nameOf={(_, i) => `expense ${i + 1}`}
      onAdd={() => onChange([...rows, { label: "", amount: "", needsReimbursement: true }])}
      onMove={move}
      onRemove={(i) => onChange(rows.filter((_, idx) => idx !== i))}
      renderFields={(row, i) => (
        // One flex row inside the record's field cell: the LABEL fills, the amount stays fixed-narrow and the
        // toggle hugs its text — RecordList's own auto-fit grid would give the amount and the toggle a 200px
        // track each.
        <div className={styles.line}>
          <Field hideLabel label={`Expense ${i + 1} label`} placeholder="e.g. Van rental" value={row.label}
            onChange={(e) => patch(i, { label: e.target.value })} />
          <Field hideLabel label={`Expense ${i + 1} amount`} className={styles.amount} type="number" inputMode="decimal"
            min="0" step="0.01" placeholder="0.00" value={row.amount} onChange={(e) => patch(i, { amount: e.target.value })} />
          <label className={styles.reimburse} title="Show this on the settle-up page. Uncheck for a recorded cost that needs no reimbursement.">
            <input type="checkbox" checked={row.needsReimbursement} onChange={(e) => patch(i, { needsReimbursement: e.target.checked })} />
            Needs reimbursement
          </label>
          {row.addedByName && (
            <span className={styles.addedBy}>
              Added by {row.addedByName}{row.hasReceipt ? " · receipt" : ""}
            </span>
          )}
        </div>
      )}
    />
  );
}
